import React, { useState } from 'react';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { SPACING_COMPACT, BORDER_RADIUS } from '../constants/layout';
import { FONT_BODY, FONT_FOOTNOTE } from '../constants/typography';

function formatPrice(amount, currency) {
  const value = amount / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(value);
}

export default function ProductGridItem({ product, coverImage, onClick }) {
  const [loaded, setLoaded] = useState(false);

  const { showPrices } = useDisplayPreferences();

  const hasPrice =
    showPrices &&
    typeof product.amount === 'number' &&
    product.amount > 0 &&
    product.currency;

  return (
    <button className="product-grid-btn" onClick={onClick}>
      <div className={`product-image-container${coverImage && coverImage.imageURL && !loaded ? ' shimmer' : ''}`}>
        {coverImage && coverImage.imageURL ? (
          <img
            src={coverImage.thumbnailURL || coverImage.imageURL}
            alt={product.productName}
            onLoad={() => setLoaded(true)}
            className={`product-grid-img ${loaded ? 'loaded' : ''}`}
          />
        ) : (
          <div className="product-placeholder">
            <span className="material-icons">image</span>
          </div>
        )}
      </div>
      <div className="product-grid-name">{product.productName}</div>
      {hasPrice && (
        <div className="product-grid-price">
          {formatPrice(product.amount, product.currency)}
        </div>
      )}
      <style jsx>{`
        .product-grid-btn {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          padding: 0;
          border: none;
          background: none;
          cursor: pointer;
          transition: transform 0.2s ease;
          text-align: left;
        }
        .product-grid-btn:hover {
          transform: translateY(-2px);
        }
        .product-grid-btn:focus {
          outline: 2px solid var(--color-link);
          outline-offset: 2px;
        }
        .product-image-container {
          position: relative;
          width: 100%;
          box-sizing: border-box;
          overflow: hidden;
          border: 1px solid var(--color-border-strong);
          border-radius: ${BORDER_RADIUS};
          background: var(--color-border);
        }
        .product-image-container.shimmer {
          background: linear-gradient(90deg, #3a3a3a 25%, #4a4a4a 50%, #3a3a3a 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s ease-in-out infinite;
        }
        .product-image-container::before {
          content: '';
          display: block;
          padding-top: 100%;
        }
        .product-grid-img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.5s ease-in;
        }
        .product-grid-img.loaded {
          opacity: 1;
        }
        .product-placeholder {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-text-muted);
        }
        .product-placeholder .material-icons {
          font-size: 40px;
        }
        .product-grid-name {
          margin-top: 0px;
          padding-top: ${SPACING_COMPACT};
          font-size: ${FONT_BODY};
          color: var(--color-text-primary);
          text-align: left;
          word-break: break-word;
          font-weight: 700;
          line-height: 1.3;
        }
        .product-grid-price {
          margin-top: 4px;
          padding-bottom: ${SPACING_COMPACT};
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-secondary);
          font-weight: 600;
          text-align: left;
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </button>
  );
}
